import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { nanoid } from "nanoid";
import { Box, CircularProgress, Link, Stack } from "@mui/material";
import { Create } from "@mui/icons-material";

import { useGetOtherStructureProtectionPriceLazyQuery } from "@covertree/new-business-graphql";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { theme } from "@covertree/theme";
import { FormToggleCard, HelpText, Modal } from "@covertree/ui";
import { formatCurrency } from "@covertree/utils";

import { ModalTitle, OtherStructureWithPrice } from "./types";
import { MineSubsidenceScheduledModalContent } from "./MineSubsidenceScheduledModalContent";

interface MineSubsidenceScheduledCardProps {
  description: string;
  image: string;
  name: string;
  tooltip: string;
  valueIsCustomizable?: boolean;
}

function MineSubsidenceScheduledCard({
  description,
  image,
  name,
  tooltip,
  valueIsCustomizable = true,
}: MineSubsidenceScheduledCardProps) {
  const [modalTitle, setModalTitle] = useState<ModalTitle>(
    ModalTitle.LimitAndDescription
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const { control, getValues, watch } = useFormContext<{
    mineSubsidenceScheduled: OtherStructureWithPrice[];
  }>();

  const { policyLocator } = useNewBusinessSelector(selectPolicy)!;
  const [getOtherStructureProtectionPrice] =
    useGetOtherStructureProtectionPriceLazyQuery();

  const { append, update, remove } = useFieldArray({
    name: "mineSubsidenceScheduled",
    control,
  });

  const fields = watch("mineSubsidenceScheduled");

  const currentPriceOfAllItems = useMemo(
    () => fields.reduce((acc, { price }) => acc + (price ?? 0), 0),
    [fields]
  );

  useEffect(() => {
    setLoading(true);

    Promise.all(
      fields.map(async (item, idx) => {
        if (item.price) return;

        const price = await getOtherStructureProtectionPrice({
          variables: {
            otherStructureDescription: item.description,
            otherStructureValue: item.value,
            policyLocator,
          },
        });

        update(idx, {
          ...item,
          price: price.data?.getOtherStructureProtectionPrice,
        });
      })
    ).finally(() => setLoading(false));
  }, [fields]);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    const values = getValues("mineSubsidenceScheduled");

    setIsModalOpen(false);
    if (values.length === 0) {
      setModalTitle(ModalTitle.LimitAndDescription);
    } else {
      setModalTitle(ModalTitle.Initial);
    }
  }, []);

  const handleAddOrUpdateItem = useCallback(
    (otherStructure: OtherStructureWithPrice) => {
      const values = getValues("mineSubsidenceScheduled");

      if (otherStructure.id) {
        const index = values.findIndex(({ id }) => id === otherStructure.id);

        if (index > -1) {
          return update(index, otherStructure);
        }
      }

      if (otherStructure.locator) {
        const index = values.findIndex(
          ({ locator }) => locator === otherStructure.locator
        );

        if (index > -1) {
          return update(index, otherStructure);
        }
      }

      return append({
        ...otherStructure,
        id: nanoid(),
      });
    },
    [append, getValues, update]
  );

  const handleDeleteItem = useCallback(
    (otherStructure: OtherStructureWithPrice) => {
      const values = getValues("mineSubsidenceScheduled");

      if (otherStructure.id) {
        const index = values.findIndex(({ id }) => id === otherStructure.id);

        if (index > -1) {
          remove(index);
          return values.length - 1;
        }
      }

      if (otherStructure.locator) {
        const index = values.findIndex(
          ({ locator }) => locator === otherStructure.locator
        );

        if (index > -1) {
          remove(index);
          return values.length - 1;
        }
      }

      return values.length;
    },
    [remove, getValues]
  );

  return (
    <FormToggleCard
      title={name}
      image={image}
      tooltip={tooltip}
      actionText={
        fields.length > 0 && currentPriceOfAllItems > 0
          ? `+ ${formatCurrency(currentPriceOfAllItems / 12, 2)}/mo`
          : undefined
      }
      actionSubText={
        fields.length > 0 && currentPriceOfAllItems > 0
          ? `(${formatCurrency(currentPriceOfAllItems, 2)}/year)`
          : undefined
      }
      subheader={description}
      subheaderVariant="grey"
      onToggle={
        fields.length > 0
          ? () => {
              remove();
            }
          : handleOpenModal
      }
      checked={fields.length > 0}
      checkedCustomStyles={{
        backgroundColor: theme.palette.secondary.contrastText,
      }}
      customizable={valueIsCustomizable}
      actions={
        fields.length > 0 ? (
          <Stack sx={{ width: "100%" }} mb={3} mt={2} spacing={1}>
            {fields.map((item) => (
              <Box key={item.id ?? item.locator} role="listitem">
                <HelpText
                  text={`${item.description}:`}
                  spanText={formatCurrency(item.value)}
                />
              </Box>
            ))}
          </Stack>
        ) : undefined
      }
      content={
        <>
          {valueIsCustomizable && (
            <Link
              component="button"
              type="button"
              onClick={handleOpenModal}
              color="secondary"
              variant="body2"
              underline="always">
              <Box
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "center",
                  "& svg": {
                    marginRight: theme.spacing(1),
                  },
                })}>
                <Create fontSize="inherit" />
                {fields.length === 0 ? "Add" : "Edit"}
              </Box>
            </Link>
          )}
          <Modal
            maxWidth="sm"
            title={modalTitle}
            open={isModalOpen}
            onClose={handleCloseModal}>
            {loading ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={(theme) => ({
                  width: theme.spacing(10),
                  height: theme.spacing(10),
                })}>
                <CircularProgress color="primary" />
              </Box>
            ) : (
              <MineSubsidenceScheduledModalContent
                addOrUpdateItem={handleAddOrUpdateItem}
                closeModal={handleCloseModal}
                deleteItem={handleDeleteItem}
                fields={fields}
                updateModalTitle={setModalTitle}
              />
            )}
          </Modal>
        </>
      }
    />
  );
}

export { MineSubsidenceScheduledCard };
